import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { useSelector, DefaultRootState, useDispatch } from "react-redux";
import IntlMessages from "../../../../../util/IntlMessages";
import { MultiSelect } from "../../../../BP2Components";
import { useChat } from "../../../../../context/ChatContext/ChatContext";
import ChatSettingsSelect, { ChatOption } from "./ChatSelect/ChatSettingsSelect";
import ChatChangeRoomName from "./ChatChangeRoomName";
import { updateUserInRoomList } from "../firebaseCRUD";
import { setChatSettings } from "../../../../../appRedux/actions/ChatSettings";
import ChatSettingsUserList from "./ChatSettingsUserList";
import { createNewRoom, findCurrentChatRoom } from "../ChatScripts";
import { v4 as uuidv4 } from "uuid";

interface ManageChatUsersProps {
   ref: any;
   setIsModalOpen: any;
}

const ManageChatUsers: React.FC<ManageChatUsersProps> = forwardRef(
   (props: ManageChatUsersProps, ref) => {
      const [selectedOptions, setSelectedOptions] = useState<ChatOption[]>([]);
      const [usersToRemove, setUsersToRemove] = useState<(string | number)[]>([]);

      const chatSettings = useSelector((state: DefaultRootState) => state.chatSettings);
      const user = useSelector((state: DefaultRootState) => state.user);

      const chatData = useChat();

      const dispatch = useDispatch();

      const userToAdd = useMemo(() => {
         const mapped =
            chatData?.chatData?.filteredPhpUsers?.map((user) => ({
               value: user?.oldId,
               label: `${user?.firstName} ${user?.lastName}`,
            })) || [];

         // some values aren`t unique (dubbled), we are removing dubbles here
         const unique = Array.from(new Map(mapped.map((item) => [item.value, item])).values());

         return unique;
      }, [chatData?.chatData?.filteredPhpUsers]);

      const handleAddUsers = (): void => {
         const currentUsersList = findCurrentChatRoom(chatData, chatSettings)?.chatUsers?.map(
            (user: any) => {
               if (typeof user === "object" && user.value) {
                  return user;
               }
               return { value: user };
            },
         );

         const selectedUsers = Array.isArray(selectedOptions) ? selectedOptions : [];

         const combinedList = [...currentUsersList, ...selectedUsers];

         const uniqueList = combinedList.reduce<ChatOption[]>((acc, current) => {
            const exists = acc.some((item) => item.value === current.value);
            if (!exists) {
               acc.push(current);
            }
            return acc;
         }, []);

         const newChatUsers = uniqueList.map((item) => item.value).sort();

         // if user is adding other users to "direct" room type, we want to create new room (direct chats cannot have more then 2 users)
         if (chatSettings?.currentRoom?.type === "direct") {
            const newRoomUsers = newChatUsers?.map((userOldId) =>
               chatData?.chatData?.filteredPhpUsers?.find((user) => user?.oldId === userOldId),
            );

            const newRoomName = newRoomUsers?.map((user) => user?.firstName).join(", ");

            const newRoom = createNewRoom(
               uuidv4(),
               "private",
               newRoomName,
               newChatUsers,
               user?.oldId,
            );

            dispatch(setChatSettings({ ...chatSettings, currentRoom: newRoom }));

            // closing settings
            props?.setIsModalOpen(false);
         } else {
            // Normal use case: ust add users to current room
            // If it was a direct room, this converts it to a private/group chat
            const updatedRoomType =
               chatSettings?.currentRoom?.type === "direct" && newChatUsers.length > 2
                  ? "private"
                  : chatSettings?.currentRoom?.type;

            updateUserInRoomList("rooms/", chatSettings?.currentRoom?.key, newChatUsers);

            dispatch(
               setChatSettings({
                  currentRoom: {
                     ...chatSettings.currentRoom,
                     chatUsers: newChatUsers.sort(),
                     type: updatedRoomType,
                  },
               }),
            );
         }

         setSelectedOptions([]);
      };

      const handleRemoveUsers = (): void => {
         if (chatSettings?.currentRoom?.chatUsers) {
            const updatedChatUsers = findCurrentChatRoom(chatData, chatSettings)?.chatUsers?.filter(
               (user) => !usersToRemove.includes(user),
            );

            updateUserInRoomList(
               "rooms/",
               chatSettings?.currentRoom?.key,
               updatedChatUsers?.sort(),
            );
            dispatch(
               setChatSettings({
                  currentRoom: {
                     ...chatSettings.currentRoom,
                     chatUsers: updatedChatUsers?.sort(),
                  },
               }),
            );
            setUsersToRemove([]);
         }
      };
      // hear me out. here we share function to parent component. We need function logic here to keep correct data structure, but function will be trigger in parent component
      useImperativeHandle(ref, () => ({
         removeUsers: handleRemoveUsers,
      }));

      return (
         <div className="new-chat-settings-container">
            <div className="new-chat-settings-container__add-users-container">
               <ChatSettingsSelect
                  options={userToAdd}
                  selectedUsers={selectedOptions}
                  setSelectedUsers={setSelectedOptions}
                  handleAdd={handleAddUsers}
                  chatSettings={chatSettings}
               />
            </div>
            <div style={{ position: "absolute", top: "10rem", width: "100%" }}>
               <ChatSettingsUserList
                  users={findCurrentChatRoom(chatData, chatSettings)?.chatUsers}
                  chatData={chatData}
                  setUsersToRemove={setUsersToRemove}
                  chatSettings={chatSettings}
               />
            </div>
         </div>
      );
   },
);

export default ManageChatUsers;
